import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { DeliverableTemplate } from '@horwath-htl/shared-types';

interface TemplateCloneDialogProps {
  open: boolean;
  onClose: () => void;
  sourceTemplate: DeliverableTemplate;
  onSuccess?: (newTemplateId: string) => void;
}

interface CloneRequest {
  sourceTemplateId: string;
  name: string;
  description?: string;
}

interface CloneResponse {
  success: boolean;
  templateId: string;
}

export const TemplateCloneDialog: React.FC<TemplateCloneDialogProps> = ({
  open,
  onClose,
  sourceTemplate,
  onSuccess,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Initialize form with source template data when dialog opens
  useEffect(() => {
    if (open && sourceTemplate) {
      setName(`${sourceTemplate.name} (Copy)`);
      setDescription(sourceTemplate.description || '');
    }
  }, [open, sourceTemplate]);

  const cloneMutation = useMutation({
    mutationFn: async () => {
      const cloneData: CloneRequest = {
        sourceTemplateId: sourceTemplate.id,
        name,
        description,
      };

      const response = await authenticatedFetch('/api/deliverables/templates/clone', {
        method: 'POST',
        body: JSON.stringify(cloneData),
      });

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Clone failed');
      }

      return await response.json();
    },
    onSuccess: (data: CloneResponse) => {
      // Invalidate templates query to refresh list
      queryClient.invalidateQueries({ queryKey: ['templates'] });

      if (onSuccess && data.templateId) {
        onSuccess(data.templateId);
      }
      handleClose();
    },
  });

  const handleClose = () => {
    if (!cloneMutation.isPending) {
      onClose();
    }
  };

  const handleClone = () => {
    cloneMutation.mutate();
  };

  const canClone = name.trim().length > 0 && name.length <= 255;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clone Template</DialogTitle>
      <DialogContent>
        {/* Source Template Display */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Source Template:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {sourceTemplate.name}
          </Typography>
        </Paper>

        {/* New Template Name */}
        <TextField
          fullWidth
          label="New Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={cloneMutation.isPending}
          error={name.length > 255}
          helperText={`${name.length}/255 characters`}
          sx={{ mb: 2 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          disabled={cloneMutation.isPending}
          sx={{ mb: 2 }}
        />

        <Alert severity="info" sx={{ mb: 2 }}>
          The cloned template will be created as a personal template owned by you.
        </Alert>

        {/* Error Display */}
        {cloneMutation.isError && (
          <Alert severity="error">
            {cloneMutation.error instanceof Error
              ? cloneMutation.error.message
              : 'An error occurred while cloning the template'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={cloneMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleClone}
          disabled={!canClone || cloneMutation.isPending}
          startIcon={cloneMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {cloneMutation.isPending ? 'Cloning...' : 'Clone Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
